import React, { useContext, useEffect, useState } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { useHistory } from "react-router-dom";
import axios from 'axios';
import {
	Typography,
	Button,
	IconButton,
	FormControlLabel,
	Divider,
	Switch,
	Paper,
	Grid,
	AppBar,
	Tabs,
	Tab,
	Toolbar,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	CircularProgress,
	Tooltip
} from "@material-ui/core";

import Alert from '@material-ui/lab/Alert';

import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import DescriptionIcon from '@material-ui/icons/Description';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { PageContext } from "../../contexts/PageContext";
import { UserContext } from "../../contexts/UserContext";
import CreditField from "./CreditField";
import CreditFieldGroupLiegenschaft from "./CreditFieldGroupLiegenschaft";
import CreditFieldGroupPerson from "./CreditFieldGroupPerson";
import CreditFieldGroupVorlast from "./CreditFieldGroupVorlast";
import CreditFieldGroupVorlastKonsum from "./CreditFieldGroupVorlastKonsum";
import CreditFiles from "./CreditFiles";
import InfoTooltip from "../general/InfoTooltip";
import CircularProgressWithLabel from "../general/CircularProgressWithLabel";
import { formatDate, areArraysIdentical, keyToLabel } from "../../functions/GlobalHelpers"
import PercentComputationHelper from "./tools/PercentComputationHelper";
import FirstPaymentDateComputationHelper from './tools/FirstPaymentDateComputationHelper';
import { useCreditFunctions } from "../../functions/CreditFunctions";
import PushCreditButton from "./PushCreditButton";
import AssignAgentDialog from "./tools/AssignAgentDialog";
import RenameCreditDialog from "./tools/RenameCreditDialog";
import CreditHistoryDialog from "./tools/CreditHistoryDialog";
import CustomerPortalPinDialog from "./tools/CustomerPortalPinDialog";

const useStyles = makeStyles((theme) => ({
	root: {
		maxWidth: 1100,
		marginLeft: "auto",
		marginRight: "auto",
		zIndex: 1,
		position: "relative"
	},
	heading: {
		top: 60,
		paddingLeft: 0,
		paddingRight: 0,
		zIndex: 2,
	},
	headingspacer: {
		paddingTop: theme.spacing(2),
		marginLeft: theme.spacing(-2),
		marginRight: theme.spacing(-2),
		marginBottom: theme.spacing(-3),
		height: theme.spacing(7),
		width: "calc(100% + " + theme.spacing(4) + "px)",
		zIndex: 0
	},
	descriptionPaper: theme.descriptionPaper,
	accordionDetailsWithTabs: {
		display: "block"
	},
	progress: theme.progress
}));

const channelDisplayNames = {
	GLOBAL: "Allgemein",
	BAWAG: "BAWAG PSK",
	START: "Start:Bausparkasse",
	BANKAUSTRIA: "Bank Austria (alt)",
	BANKAUSTRIAAPI: "Bank Austria",
	ING: "Bank99",
	EXCEL: "Excel Export",
};

export default function Credit({ id, view }) {
	const theme = useTheme();
	const classes = useStyles();
	let history = useHistory();
	const [credit, setCredit] = useState({});
	const [portals, setPortals] = useState([]);
	const [loading, setLoading] = useState(false);
	const [channels, setChannels] = useState([]);
	const [metafields, setMetafields] = useState([]);
	// die configs aus den channels
	const [fieldConfigsByMetaKey, setFieldConfigsByMetaKey] = useState({});
	// die Metafield Objekte gruppiert nach meta_key
	const [fieldsByMetaKey, setFieldsByMetaKey] = useState({});
	const [metafieldsSaveTimeouts, setMetafieldsSaveTimeouts] = useState({});
	const [activeTabLiegenschaft, setActiveTabLiegenschaft] = useState(0);
	const [activeTabPerson, setActiveTabPerson] = useState(0);
	const [activeTabVorlast, setActiveTabVorlast] = useState(0);


	const pageContext = useContext(PageContext);
	const userContext = useContext(UserContext);

	const metaKeyGroups = {
		kosten: ["kaufpreis", "rechtsanwalt_notar", "makler", "sonstige_kosten", "neben_und_aufschliessungskosten", "bau_umbaukosten", "zusaetzlicher_bestandswert", "fuer_simultan_hbh", "fuer_simultan_hbh_hinweis"]
	}




	useEffect(() => {
		if (id && (!credit || !credit.id || credit.id !== id)) {
			getCredit();
			setLoading(true);
		} else if (!id) {
			setCredit({});
			setChannels([]);
			setMetafields([]);
		}
	}, [id]);


	useEffect(() => {
		setFieldsByMetaKey(newFieldsByMetaKey => {
			const isFirstRun = Object.keys(newFieldsByMetaKey).length === 0;
			metafields.forEach(field => {
				if (field.meta_key in newFieldsByMetaKey) {
					const existingFieldIndex = newFieldsByMetaKey[field.meta_key].findIndex(f => f.id === field.id);
					if (existingFieldIndex > -1) {
						if (newFieldsByMetaKey[field.meta_key][existingFieldIndex].meta_value !== field.meta_value
							|| newFieldsByMetaKey[field.meta_key][existingFieldIndex].meta_value_numeric !== field.meta_value_numeric
							|| newFieldsByMetaKey[field.meta_key][existingFieldIndex].meta_value_boolean !== field.meta_value_boolean
							|| newFieldsByMetaKey[field.meta_key][existingFieldIndex].file_document_type !== field.file_document_type
						)
							newFieldsByMetaKey[field.meta_key][existingFieldIndex] = field;
					} else {
						newFieldsByMetaKey[field.meta_key].push(field);
					}
				} else {
					newFieldsByMetaKey[field.meta_key] = [field];
				}
			});

			// Wenn das Objekt zum ersten mal befüllt wird möchte ich, dass dies ein Re-Render ausführt, ansonsten nicht.
			return isFirstRun ? { ...newFieldsByMetaKey } : newFieldsByMetaKey;
		});


		setFieldConfigsByMetaKey(newFieldConfigs => {
			const isFirstRun = Object.keys(newFieldConfigs).length === 0;
			let hasChanged = false;
			metafields.forEach(field => {
				const fieldConfigs = creditFunctions.getConfigsForField(field.meta_key, true, true);

				if (credit.type === "konsum" && field.meta_key == 'kaufpreis') {
					// wenn es ein Konsumkredit ist, dann soll der Kaufpreis umbenannt werden
					fieldConfigs[0].label = 'Finanzierungsbedarf';
				}
				if (credit.type === "konsum" && field.meta_key == 'finanzierungslaufzeit') {
					// wenn es ein Konsumkredit ist, dann soll die maximale Laufzeit auf 120 Monate gesetzt werden
					fieldConfigs[0].max = 120;
				}

				// prüft ob die configs bereits vorhanden sind und gleich lang sind
				// gleich lang spielt eine Rolle wenn ein Feld (oder eine config eines Feldes) aufgrund von Conditions ein- oder ausgeblendet wird
				if (field.meta_key in newFieldConfigs && newFieldConfigs[field.meta_key].length === fieldConfigs.length) {
					// hier können sich eigentlich nur mehr die Channels ändern. 
					// einer könnte aus- oder eingeblendet werden
					if (newFieldConfigs[field.meta_key].some((existingConfig, i) => {
						return !areArraysIdentical(existingConfig.channels, fieldConfigs[i].channels)
					})) {
						newFieldConfigs[field.meta_key] = fieldConfigs;
						hasChanged = true;
					}
				} else {
					newFieldConfigs[field.meta_key] = fieldConfigs;
					hasChanged = true;
				}
			});
			// Wenn das Objekt zum ersten mal befüllt wird möchte ich, dass dies ein Re-Render ausführt, ansonsten nicht.
			return (isFirstRun || hasChanged) ? { ...newFieldConfigs } : newFieldConfigs;
			//return isFirstRun ? {...newFieldConfigs} : newFieldConfigs;
		})
	}, [metafields, channels]);

	useEffect(() => {
		if (loading && credit && credit.id && Object.keys(fieldConfigsByMetaKey).length > 0 && Object.keys(fieldsByMetaKey).length > 0)
			setLoading(false);
	}, [loading, credit, fieldConfigsByMetaKey, fieldsByMetaKey]);

	const getCredit = (action = null, action_param = null) => {
		let url = `${process.env.REACT_APP_API_SERVICE_URL}/credits/${id}`;
		if (action) {
			url += "/" + action;
			if (action_param)
				url += "/" + action_param;
		}
		const options = {
			url: url,
			method: 'get',
			headers: userContext.getRequestAuthHeader()
		};
		axios(options)
			.then((res) => {
				if (action === "refreshfieldmapping") {
					pageContext.setInfoMessage("Schnittstellen wurden neu geladen");
					window.location.reload();
				}
				const newCredit = { ...res.data };
				// verbundene Objekte entfernen und separat im State speichern
				delete newCredit.channels;
				delete newCredit.metafields;
				setCredit(newCredit);
				let newChannels = [];
				// sort channels by channelDisplayname list
				res.data.channels.forEach(channel => {
					const channel_index = Object.keys(channelDisplayNames).indexOf(channel.channel_shortname);
					newChannels[channel_index] = channel;
				});
				// remove empty indexes from array
				newChannels = newChannels.filter(channel => channel !== undefined);

				setChannels(newChannels);
				setMetafields(res.data.metafields);
				getPortals();
			})
			.catch((err) => {
				console.log(err);
			});
	}

	const getPortals = () => {
		let url = `${process.env.REACT_APP_API_SERVICE_URL}/portals/${id}`;
		const options = {
			url: url,
			method: 'get',
			headers: userContext.getRequestAuthHeader(),
		};

		axios(options)
			.then((res) => {
				setPortals(res.data);
			})
			.catch((err) => {
				console.log(err);
			});
	}

	const createPortal = (personIndex, portalPin = "", callback = null) => {
		let url = `${process.env.REACT_APP_API_SERVICE_URL}/portals/${id}`;
		const options = {
			url: url,
			data: {
				person_index: personIndex,
				portal_pin: portalPin,
			},
			method: 'post',
			headers: userContext.getRequestAuthHeader()
		};
		axios(options)
			.then((res) => {
				getPortals();
				if (callback)
					callback();
			})
			.catch((err) => {
				console.log(err);
			});
	}


	const savePortal = (portal, callback = null) => {
		const options = {
			url: `${process.env.REACT_APP_API_SERVICE_URL}/portals/${credit.id}/${portal.id}`,
			method: 'put',
			data: portal,
			headers: userContext.getRequestAuthHeader()
		};
		axios(options)
			.then((res) => {
				getPortals();
				if (callback)
					callback();
			})
			.catch((err) => {
				console.log(err);
				pageContext.setErrorMessage("Die Änderung für das Kundenportal konnte nicht gespeichert werden!");
			});
	}


	const saveCredit = (silent) => {
		const saveData = { ...credit, channels: [] };
		channels.forEach(channel => {
			saveData.channels.push({ id: channel.id, enabled: channel.enabled });
		});
		const options = {
			url: `${process.env.REACT_APP_API_SERVICE_URL}/credits/${credit.id}`,
			method: 'put',
			data: saveData,
			headers: userContext.getRequestAuthHeader()
		};
		axios(options)
			.then((res) => {
				if (!silent)
					pageContext.setInfoMessage("Finanzierung wurde gespeichert");
			})
			.catch((err) => {
				console.log(err);
				if (!silent)
					pageContext.setErrorMessage("Finanzierung konnte nicht gespeichert werden!");
			});
	}


	useEffect(() => {
		if (!loading && credit && credit.id && channels)
			saveCredit(true);
	}, [channels]);


	const refreshMetafieldFromServer = (metafield) => {
		const options = {
			url: `${process.env.REACT_APP_API_SERVICE_URL}/metafields/${metafield.id}`,
			data: {
				id: metafield.id
			},
			method: 'get',
			headers: userContext.getRequestAuthHeader()
		};
		axios(options)
			.then(({ data }) => {
				setMetafields(fields => {
					const index = fields.findIndex(field => field.id === metafield.id);
					if (index > -1)
						fields[index] = data

					return [...fields];
				})
			})
			.catch((err) => {
				console.log(err);
				pageContext.setErrorMessage("Das Feld konnte nicht geladen werden!");
			});
	}

	const getMetafieldsByFieldConfig = (fieldConfig) => {
		return metafields.filter(field => field.meta_key === fieldConfig.meta_key && fieldConfig.channels.includes(field.channel));
	}



	const maybeUpdateLaufzeitFields = (fields, currentMetafields, value, updatedIDs) => {
		if (
			currentMetafields[0].meta_key === "finanzierungslaufzeit" || currentMetafields[0].meta_key.endsWith("__geburtsdatum")
		) {
			for (let i = 1; i <= 4; i++) {
				const field_finanzierungslaufzeit = fields.find(field => field.meta_key === "finanzierungslaufzeit");
				const field_person__geburtsdatum = fields.find(field => field.meta_key === `__person${i}__geburtsdatum`);
				const field_alter_laufzeitende = fields.find(field => field.meta_key === `__person${i}__alter_laufzeitende`);


				if (field_finanzierungslaufzeit && field_person__geburtsdatum && field_alter_laufzeitende) {
					const today = new Date();
					const estimatedStartDate = new Date(today.getFullYear(), today.getMonth() + 2, 1); // estimate 2 month in the future as start date
					const laufzeit = field_finanzierungslaufzeit.meta_value_numeric || 0;
					if (laufzeit > 0 && field_person__geburtsdatum.meta_value && field_person__geburtsdatum.meta_value !== "") {
						const geburtsdatum = new Date(field_person__geburtsdatum.meta_value);
						const ageAtStart = (estimatedStartDate.getFullYear() - geburtsdatum.getFullYear()) - (estimatedStartDate.getMonth() < geburtsdatum.getMonth() || (estimatedStartDate.getMonth() === geburtsdatum.getMonth() && estimatedStartDate.getDate() < geburtsdatum.getDate()) ? 1 : 0);
						const ageAtEnd = ageAtStart + laufzeit / 12;
						console.log("ageAtStart: ", ageAtStart, "ageAtEnd: ", ageAtEnd);
						if (ageAtEnd > 0) {
							// übernächstes Monat am ausgewählten Tag (1. / 5. / 15.) + tilgungsfreie Monate
							fields = creditFunctions.setMetafieldValue(
								fields,
								field_alter_laufzeitende,
								ageAtEnd
							);
							updatedIDs.push(field_alter_laufzeitende.id);
						}
					}
				}
			}

		}

		return { fields, updatedIDs }
	}

	const setMetafieldsValues = (currentMetafields, value, forceRerender = false) => {
		setMetafields(fields => {
			let updatedIDs = [];
			let relatedFieldsAffected = false;
			let conditionsAffected = false;
			currentMetafields.forEach(metafield => {
				fields = creditFunctions.setMetafieldValue(fields, metafield, value);

				updatedIDs.push(metafield.id);
			})

			let fieldUpdates = maybeUpdateLaufzeitFields(fields, currentMetafields, value, updatedIDs);
			updatedIDs = fieldUpdates.updatedIDs;
			fields = fieldUpdates.fields;


			const currentFieldConfig = channels
				.find(channel => channel.channel_shortname === currentMetafields[0].channel)
				.fieldmapping.find(fieldmapping => fieldmapping.meta_key === currentMetafields[0].meta_key);

			// try to set related fields 
			// "related" means same key and a compatible type
			// but there may be other relations for example Geburtsland and Nationalität
			const relatedMetaKeys = [currentMetafields[0].meta_key];
			if (currentMetafields[0].meta_key.indexOf('__geburtsland')) {
				relatedMetaKeys.push(currentMetafields[0].meta_key.replace('__geburtsland', '__nationalitaet'));
			}

			if (currentMetafields[0].meta_key.indexOf('__beruflicher_status') && value === "PENSION") {
				const metafield__arbeitgeber_name = fields.find(field => field.meta_key === currentMetafields[0].meta_key.replace('__beruflicher_status', '__arbeitgeber_name'));
				if (metafield__arbeitgeber_name) {
					creditFunctions.setMetafieldValue(fields, metafield__arbeitgeber_name, "Pensionsversicherunsanstalt");
					updatedIDs.push(metafield__arbeitgeber_name.id);
				}
				const metafield__arbeitgeber_strasse = fields.find(field => field.meta_key === currentMetafields[0].meta_key.replace('__beruflicher_status', '__arbeitgeber_strasse'));
				if (metafield__arbeitgeber_strasse) {
					creditFunctions.setMetafieldValue(fields, metafield__arbeitgeber_strasse, "Eggenberger Straße");
					updatedIDs.push(metafield__arbeitgeber_strasse.id);
				}
				const metafield__arbeitgeber_hausnummer = fields.find(field => field.meta_key === currentMetafields[0].meta_key.replace('__beruflicher_status', '__arbeitgeber_hausnummer'));
				if (metafield__arbeitgeber_hausnummer) {
					creditFunctions.setMetafieldValue(fields, metafield__arbeitgeber_hausnummer, "3");
					updatedIDs.push(metafield__arbeitgeber_hausnummer.id);
				}
				const metafield__arbeitgeber_plz = fields.find(field => field.meta_key === currentMetafields[0].meta_key.replace('__beruflicher_status', '__arbeitgeber_plz'));
				if (metafield__arbeitgeber_plz) {
					creditFunctions.setMetafieldValue(fields, metafield__arbeitgeber_plz, "8020");
					updatedIDs.push(metafield__arbeitgeber_plz.id);
				}
				const metafield__arbeitgeber_ort = fields.find(field => field.meta_key === currentMetafields[0].meta_key.replace('__beruflicher_status', '__arbeitgeber_ort'));
				if (metafield__arbeitgeber_ort) {
					creditFunctions.setMetafieldValue(fields, metafield__arbeitgeber_ort, "Graz");
					updatedIDs.push(metafield__arbeitgeber_ort.id);
				}
			}

			// do not check related fields if the current field is a multiple field
			const relatedFields = currentMetafields[0].mutiple ? [] : fields.filter(
				field =>
					relatedMetaKeys.includes(field.meta_key)
					&& !updatedIDs.includes(field.id) // not already updated
			);


			relatedFields.forEach(relatedField => {
				if (
					(
						(
							currentMetafields[0].field_type === "STRING_ENUM"
							&& relatedField.field_type === "STRING_ENUM"
						)
						|| (
							currentMetafields[0].field_type === "INT_ENUM"
							&& relatedField.field_type === "INT_ENUM"
						)
					)
					&& !relatedField.meta_value) {
					// wenn beide vom Typ _ENUM sind prüfen ob das andere Feld den Wert in den Options enthält
					// die Options können entweder ein Array von Werten oder ein Array von {value: ..., label: ....}-Paaren sein 
					const relatedFieldConfig = channels
						.find(channel => channel.channel_shortname === relatedField.channel)
						.fieldmapping.find(fieldmapping => fieldmapping.meta_key === relatedField.meta_key);
					console.log(relatedFieldConfig);
					if (relatedFieldConfig && relatedFieldConfig.options && relatedFieldConfig.options.length) {
						// bei STRING_ENUM kann auch nur auf Labels verglichen werden, aber nur wenn current options label-value-Objekte sind
						let currentOptionLabel = null;
						if (currentFieldConfig
							&& currentFieldConfig.options
							&& currentFieldConfig.options.length
							&& currentFieldConfig.field_type === "STRING_ENUM") {
							const currentOption = currentFieldConfig.options.find(option =>
								(typeof option !== "string" && option.value === value));
							if (currentOption)
								currentOptionLabel = currentOption.label;
						}

						let matchingOption = relatedFieldConfig.options.find(option => {
							if (currentMetafields[0].field_type === "STRING_ENUM") {
								// string options
								if (typeof option === "string") {
									return option.toLowerCase() === value.toLowerCase()
										|| keyToLabel(option) === keyToLabel(value)
										|| option === currentOptionLabel;
								}
								// label-value-object options
								return option.value.toLowerCase() === value.toLowerCase()
									|| option.label === keyToLabel(value)
									|| option.label === currentOptionLabel;
							} else if (currentMetafields[0].field_type === "INT_ENUM") {
								if (!isNaN(option) && option === value) {
									return true;
								} else if (option.value === value) {
									return true;
								} else if (option.label === currentOptionLabel) {
									return true;
								}
								return false;
							}
						}
						);

						if (!matchingOption) {
							matchingOption = creditFunctions.matchOptionByLookupList(currentFieldConfig, value, relatedFieldConfig.options);
						}

						if (matchingOption) {
							if (typeof matchingOption === "string" || !isNaN(matchingOption))
								fields = creditFunctions.setMetafieldValue(fields, relatedField, matchingOption);
							else
								fields = creditFunctions.setMetafieldValue(fields, relatedField, matchingOption.value);
							relatedFieldsAffected = true;
						}
					}
				} else if (
					(
						(
							currentMetafields[0].field_type === "INT_ENUM"
							&& relatedField.field_type === "STRING_ENUM"
						)
						|| (
							currentMetafields[0].field_type === "STRING_ENUM"
							&& relatedField.field_type === "INT_ENUM"
						)
					)
					&& !relatedField.meta_value) {
					const relatedFieldConfig = channels
						.find(channel => channel.channel_shortname === relatedField.channel)
						.fieldmapping.find(fieldmapping => fieldmapping.meta_key === relatedField.meta_key);
					if (relatedFieldConfig && relatedFieldConfig.options && relatedFieldConfig.options.length) {
						// only match on labels and only if both have a label
						const currentOption = currentFieldConfig.options.find(option =>
							(typeof option !== "string" && option.value === value));
						if (currentOption) {
							let currentOptionLabel = currentOption.label;
							console.log("currentOptionLabel", currentOptionLabel);
							let matchingOption = relatedFieldConfig.options.find(option => {
								if (typeof option !== "string" && option.hasOwnProperty("label")) {
									// label-value-object options
									return option.label === currentOptionLabel;
								}
								return false;
							});
							if (matchingOption) {
								fields = creditFunctions.setMetafieldValue(fields, relatedField, matchingOption.value);
								relatedFieldsAffected = true;
							}
						}
					}
				} else if (
					currentMetafields[0].field_type === relatedField.field_type
					&& !relatedField.meta_value) {
					// same type
					fields = creditFunctions.setMetafieldValue(fields, relatedField, value);
					relatedFieldsAffected = true;
				} else if (
					currentMetafields[0].field_type === "STRING_ENUM"
					&& relatedField.field_type === "STRING"
					&& !relatedField.meta_value) {
					// STRING_ENUM to STRING
					fields = creditFunctions.setMetafieldValue(fields, relatedField, keyToLabel(value));
					relatedFieldsAffected = true;
				}
			});



			// check whether this field is used in any fields conditions
			conditionsAffected = channels
				.some(channel => {
					return channel.fieldmapping.some(conditionFieldConfig => {
						if (!conditionFieldConfig.conditions)
							return false;
						if (conditionFieldConfig.conditions.length === 0)
							return false;
						return conditionFieldConfig.conditions.some(condition =>
							condition.meta_key === currentMetafields[0].meta_key
						);
					});
				});
			//console.log("conditionsAffected", conditionsAffected);
			if (forceRerender || relatedFieldsAffected || conditionsAffected)
				return [...fields];
			return fields;
		})
	}



	const isFieldVisible = (fieldConfig) => {
		//console.log("isFieldVisible: fieldConfig.meta_key", fieldConfig.meta_key)
		if (!channels || channels.length === 0)
			return false;


		// if channel disabled -> hide field
		if (!channels.find(channel => channel !== undefined && fieldConfig.channels.includes(channel.channel_shortname) && channel.enabled))
			return false;

		if (!fieldConfig.conditions || fieldConfig.conditions.length === 0)
			return true;



		let is_visible = true;
		fieldConfig.conditions.forEach(condition => {
			if (condition.meta_key) {
				let condition_metafield = metafields.find(field => field.meta_key === condition.meta_key && (fieldConfig.channels.includes(field.channel) || field.channel === "GLOBAL"));

				if (condition_metafield) {
					const value = creditFunctions.getMetafieldValue(condition_metafield);
					// if (condition.condition === "notempty")
					// 	console.log(condition_metafield,!value ? ">>empty": ">>notempty");
					// ignore condition if the field doesn't exist (anymore)
					if (condition.condition === "==" && value !== condition.meta_value)
						is_visible = false;
					if (condition.condition === "!=" && value === condition.meta_value)
						is_visible = false;
					if (condition.condition === ">" && (value === undefined || value === null || value <= condition.meta_value))
						is_visible = false;
					if ((condition.condition === ">=" || condition.condition === "=>") && (value === undefined || value === null || value < condition.meta_value))
						is_visible = false;
					if (condition.condition === "<" && value >= condition.meta_value)
						is_visible = false;
					if ((condition.condition === "<=" || condition.condition === "=<") && value > condition.meta_value)
						is_visible = false;
					if (condition.condition === "notempty" && !value)
						is_visible = false;
					if (condition.condition === "empty" && value)
						is_visible = false;
					if (condition.condition === "in" && !condition.meta_value.includes(value))
						is_visible = false;
				}
			}
		})

		return is_visible;
	}


	const creditFunctions = useCreditFunctions(
		channels,
		setMetafieldsValues,
		metafieldsSaveTimeouts,
		setMetafieldsSaveTimeouts,
		pageContext,
		userContext,
		getMetafieldsByFieldConfig,
		isFieldVisible,
	);






	const renderCreditField = (meta_key, xs = 12, md = 12, fieldlayout = "") => {
		return <CreditField
			setMetafieldsValues={setMetafieldsValues}
			resolvePlaceholders={creditFunctions.resolvePlaceholders}
			channelDisplayNames={channelDisplayNames}
			metafieldsForCurrentMetaKey={fieldsByMetaKey[meta_key]}
			fieldConfigs={fieldConfigsByMetaKey[meta_key]}
			meta_key={meta_key}
			xs={xs}
			md={md}
			fieldlayout={fieldlayout}
			credit={credit}
		/>
	}

	if (!credit || loading)
		return <>
			<CircularProgress
				className={classes.progress}
				color="secondary"
			/>
		</>;

	if (view === "dokumente")
		return <CreditFiles
			credit={credit}
			renderCreditField={renderCreditField}
			creditFunctions={creditFunctions}
			metafields={metafields}
			setMetafields={setMetafields}
			channels={channels}
			channelDisplayNames={channelDisplayNames}
		/>

	const personFieldGroupPercents = [0, 0, 0, 0];
	let numPersons = 0;
	for (let i = 0; i < 4; i++) {
		personFieldGroupPercents[i] = creditFunctions.getFieldGroupProgress("person", i + 1);
		if (personFieldGroupPercents[i] > 20) {
			numPersons++;
		}
	}


	return (
		<>
			<AppBar
				position="fixed"
				className={classes.heading}
				color="primary"
				elevation={0}
			>
				<Typography align="center" variant="h3">
					Finanzierung – {credit.title}
					<RenameCreditDialog
						credit={credit}
						setCredit={setCredit}
						saveCredit={saveCredit}
					/>
				</Typography>
			</AppBar>
			<AppBar
				position="relative"
				className={classes.headingspacer}
				color="primary"
				elevation={0}
				align="left"
			>
				<Toolbar variant="dense">
					<IconButton
						color="inherit"
						onClick={() => {
							history.push("/finanzierungen/");
						}}>
						<ArrowBackIcon />
					</IconButton>
				</Toolbar>
			</AppBar>
			<div className={classes.root}>
				<Paper className={classes.descriptionPaper}>
					{credit.type === "konsum" ?
						<Typography variant="h5">Konsumkredit</Typography>
						:
						<Grid container spacing={4}>
							<Grid item xs={12}>
								<Typography align="center" variant="overline">
									{credit.type == "konsum" ? "Konsumkredit" : "Hypothekarkredit"}
									{credit.subtype ? " – " + credit.subtype : ""}
								</Typography>
							</Grid>
							<Grid item xs={12} md={8}>
								<Typography variant="h5">Banken</Typography>
								<Typography>Für welche Banken möchtest du diesen Antrag vorbereiten? Abhängig von den gewählten Banken ändern sich auch manche Felder, die auszufüllen sind.</Typography>
							</Grid>
							<Grid item xs={12} md={4} align="right">
								<Button
									size="small"
									onClick={() => {
										getCredit("refreshfieldmapping")
									}}>
									Schnittstellen neu laden
								</Button>
								<InfoTooltip
									content={"Sollte sich eine Schnittstelle ändern, kannst du bestehende Kreditanträge nicht mehr übermitteln. In solchen Fällen kann es nötig sein die Schnittstellen zu aktualisieren."}
								/>
							</Grid>
							<Grid item xs={12}>
								{channels.map((channel, channel_index) => {
									if (channel.channel_shortname !== "GLOBAL")
										return (
											<React.Fragment key={channel.channel_shortname + "-enabled"}>
												<FormControlLabel
													control={
														<Switch
															checked={channel.enabled}
															onChange={(e) => {
																setChannels(channels => {
																	channels[channel_index].enabled = e.target.checked;
																	return [...channels]; // spread operator to make sure react recognizes the change and rerenders the field
																})
															}}
															disabled={channel.channel_shortname === "ING" && !channel.enabled /* sobald der channel einmal deaktiviert ist lässt er sich nicht mehr aktivieren */}
															color="primary"
														/>
													}
													label={channelDisplayNames[channel.channel_shortname]}
												/>
												<InfoTooltip
													content={
														<>
															Version vom {formatDate(channel.version_date)}
														</>
													}
												/>
											</React.Fragment>
										);
								})}
							</Grid>
						</Grid>
					}
				</Paper>
				<Accordion TransitionProps={{ unmountOnExit: true }}>
					<AccordionSummary
						expandIcon={<ExpandMoreIcon />}
					>
						<Typography variant="h5">Kreditnehmer</Typography>
						{[0, 1, 2, 3].map(index =>
							<CircularProgressWithLabel percent={personFieldGroupPercents[index]} key={index} />
						)}
					</AccordionSummary>
					<AccordionDetails className={classes.accordionDetailsWithTabs}>
						<AppBar position="static" color="default">
							<Tabs
								value={activeTabPerson}
								indicatorColor="primary"
								textColor="primary"
								onChange={(e, value) => {
									setActiveTabPerson(value);
								}}
							>
								{[0, 1, 2, 3].map(index => {
									let tabLabel = creditFunctions.resolvePlaceholders("[__person" + (index + 1) + "__vorname] [__person" + (index + 1) + "__nachname]");
									if (tabLabel.length < 5)
										tabLabel = "Kreditnehmer " + (index + 1);
									return (
										<Tab
											label={tabLabel}
											key={index}
											icon={
												<CircularProgressWithLabel percent={personFieldGroupPercents[index]} />
											}
										/>
									)
								})
								}
							</Tabs>
						</AppBar>
						<br />
						{[0, 1, 2, 3].map(index => {
							if (activeTabPerson === index)
								return (
									<Grid container spacing={4} role="tabpanel" key={index}>
										{creditFunctions.getFirstValueByMetaKey("__person" + (index + 1) + "__monatliches_nettoeinkommen") === 0 &&
											<Alert severity="warning">Wenn ein Kreditnehmer kein Einkommen hat ist es meistens besser für die Bewertung diesen nicht anzugeben. In diesem Fall lösche diesen einfach.</Alert>
										}
										<CreditFieldGroupPerson
											renderCreditField={renderCreditField}
											creditFunctions={creditFunctions}
											createPortal={createPortal}
											savePortal={savePortal}
											portals={portals}
											index={index + 1}
											credit_id={id}
											metafields={metafields}
											setMetafields={setMetafields}
											isOnlyPerson={numPersons === 1 && personFieldGroupPercents[index] > 20}
										/>
									</Grid>
								);
						})
						}
					</AccordionDetails>
				</Accordion>
				{credit.type === "hypothekar" &&
					<Accordion TransitionProps={{ unmountOnExit: true }}>
						<AccordionSummary
							expandIcon={<ExpandMoreIcon />}
						>
							<Typography variant="h5">Finanzierung</Typography>
						</AccordionSummary>
						<AccordionDetails>
							<Grid container spacing={4}>
								{renderCreditField("sales_partner_center_id", 12, 4)}
								{renderCreditField("branch_of_choice_id", 12, 4)}
								{renderCreditField("vermittlerverguetung", 12, 3)}
								{renderCreditField("vermittlerverguetung_prozent", 12, 3, "percent")}
								{renderCreditField("processing_fee_sales_partner", 12, 3, "euro")}
								{renderCreditField("finanzierung_zweck", 12, 6)}
								{renderCreditField("finanzierung_fuer")}
								{renderCreditField("objekt_nutzung", 6, 4)}
								{renderCreditField("neuer_eigentuemer", 6, 4)}
								{renderCreditField("objekt_wird_zweitwohnsitz", 12, 4)}
								<Grid item xs={12} md={12}>

								</Grid>
								{renderCreditField("bauart", 12, 12)}
								{renderCreditField("bauart_besonderheit", 6, 4)}
								{renderCreditField("baurecht", 6, 4)}
								{renderCreditField("geteilt_nach_weg", 6, 4)}
								{renderCreditField("familienverkauf", 6, 4)}
								{renderCreditField("zwangsversteigerung", 6, 4)}
								{renderCreditField("bundesland", 6, 3)}
								{renderCreditField("in_der_region", 6, 3)}
								{renderCreditField("einwohner_im_ort", 6, 3)}
								{renderCreditField("umgebung", 6, 3)}
								{renderCreditField("art_der_sicherstellung", 12, 12)}
							</Grid>
						</AccordionDetails>
					</Accordion>
				}
				{credit.type === "hypothekar" ?
					<Accordion TransitionProps={{ unmountOnExit: true }}>
						<AccordionSummary
							expandIcon={<ExpandMoreIcon />}
						>
							<Typography variant="h5">Kostenplanung</Typography>
						</AccordionSummary>
						<AccordionDetails>
							<Grid container spacing={4}>
								{renderCreditField("kaufpreis", 12, 7, "pricetable")}
								{renderCreditField("purchase_price_relevant_for_additional_costs", 12, 5)}
								{renderCreditField("grundpreis", 12, 7, "pricetable")}
								{renderCreditField("lot_price_relevant_for_additional_costs", 12, 5)}
								{renderCreditField("rechtsanwalt_notar", 12, 7, "pricetable")}
								<Grid item xs={12} md={5}>
									<PercentComputationHelper
										title="Anwalt/Notar"
										description="Anwälte und Notare verlangen üblicherweise ca 2% zzgl. Ust"
										sourceKey={["kaufpreis", "grundpreis"]}
										targetKey="rechtsanwalt_notar"
										defaultPercent={2.0}
										showOptionalTax={true}
										creditFunctions={creditFunctions}
									/>
								</Grid>
								{renderCreditField("makler", 12, 7, "pricetable")}
								<Grid item xs={12} md={5}>
									<PercentComputationHelper
										title="Makler"
										description="Ein übliches Maklerhonorar beträgt 3% zzgl. Ust"
										sourceKey="kaufpreis"
										targetKey="makler"
										defaultPercent={3.0}
										showOptionalTax={true}
										creditFunctions={creditFunctions}
									/>
								</Grid>
								{renderCreditField("neben_und_aufschliessungskosten", 12, 7, "pricetable")}
								{renderCreditField("development_costs_relevant_for_additional_costs", 12, 5)}
								{renderCreditField("bau_umbaukosten", 12, 7, "pricetable")}
								{renderCreditField("construction_costs_relevant_for_additional_costs", 12, 5)}
								{renderCreditField("sanierungskosten", 12, 7, "pricetable")}
								{renderCreditField("inventarkosten", 12, 7, "pricetable")}
								{renderCreditField("sonderausstattung", 12, 7, "pricetable")}
								{renderCreditField("sonstige_kosten", 12, 7, "pricetable")}
								{renderCreditField("hinweis_sonstige_kosten", 12, 7)}
								{renderCreditField("zusaetzlicher_bestandswert", 12, 7, "pricetable")}
								{renderCreditField("fuer_simultan_hbh", 12, 4)}
								{renderCreditField("fuer_simultan_hbh_hinweis", 12, 8)}
								{renderCreditField("geleistete_anzahlung", 12, 7, "pricetable")}
								{renderCreditField("eigenleistung", 12, 7, "pricetable")}
							</Grid>
						</AccordionDetails>
					</Accordion>
					:
					<Accordion TransitionProps={{ unmountOnExit: true }}>
						{/** Kostenplanung Konsumkredit */}
						<AccordionSummary
							expandIcon={<ExpandMoreIcon />}
						>
							<Typography variant="h5">Kostenplanung</Typography>
						</AccordionSummary>
						<AccordionDetails>
							<Grid container spacing={4}>
								{renderCreditField("kaufpreis", 12, 7, "pricetable")}
							</Grid>
						</AccordionDetails>
					</Accordion>
				}
				{credit.type === "hypothekar" &&
					<Accordion TransitionProps={{ unmountOnExit: true }}>
						<AccordionSummary
							expandIcon={<ExpandMoreIcon />}
						>
							<Typography variant="h5">Eigenmittel</Typography>
						</AccordionSummary>
						<AccordionDetails>
							<Grid container spacing={4}>
								{renderCreditField("eigenmittel", 12, 6, "pricetable")}
								{renderCreditField("eigenmittelreserve", 12, 6, "pricetable")}
								{renderCreditField("rueckkauf_versicherung", 12, 6, "pricetable")}
								<Grid item xs={12} md={12}>
									<Divider />
								</Grid>
								{renderCreditField("bausparguthaben", 12, 6, "pricetable")}
								{renderCreditField("vorfinanzieren_bausparguthaben", 12, 3)}
								{renderCreditField("vorfinanzieren_bausparguthaben_laufzeit", 12, 3)}
								<Grid item xs={12} md={12}>
									<Divider />
								</Grid>
								{renderCreditField("genossenschaftsanteil", 12, 6, "pricetable")}
								{renderCreditField("vorfinanzieren_genossenschaftsanteil", 12, 3)}
								{renderCreditField("vorfinanzieren_genossenschaftsanteil_laufzeit", 12, 3)}
								{renderCreditField("genossenschaftsanteil_abtretung_rueckforderungsansprueche", 12, 6)}
								{renderCreditField("genossenschaftsanteil_abtretung_rueckforderungsansprueche_art", 12, 6)}
								<Grid item xs={12} md={12}>
									<Divider />
								</Grid>
								{renderCreditField("immobilienerloes", 12, 6, "pricetable")}
								{renderCreditField("vorfinanzieren_immobilienerloes", 12, 3)}
								{renderCreditField("vorfinanzieren_immobilienerloes_laufzeit", 12, 3)}
								{renderCreditField("hinweis_immobilienerloes", 12, 6, "multiline")}
								{renderCreditField("immobilienerloes_rangordnungseintragung", 12, 6)}
								<Grid item xs={12} md={12}>
									<Divider />
								</Grid>
								{renderCreditField("sonstige_eigenmittel", 12, 6, "pricetable")}
								{renderCreditField("vorfinanzieren_sonstige_eigenmittel", 12, 3)}
								{renderCreditField("vorfinanzieren_sonstige_eigenmittel_laufzeit", 12, 3)}
								{renderCreditField("hinweis_sonstige_eigenmittel", 12, 6, "multiline")}
							</Grid>
						</AccordionDetails>
					</Accordion>
				}
				{credit.type === "hypothekar" &&
					<Accordion TransitionProps={{ unmountOnExit: true }}>
						<AccordionSummary
							expandIcon={<ExpandMoreIcon />}
						>
							<Typography variant="h5">Sicherheiten</Typography>
						</AccordionSummary>
						<AccordionDetails>
							<Grid container spacing={4}>
								{[1, 2, 3].map(index =>
									<React.Fragment key={index}>
										{renderCreditField("sonstige_eigenmittel_als_sicherheit_art" + index, 8, 4)}
										{renderCreditField("sonstige_eigenmittel_als_sicherheit_wert" + index, 8, 2, "euro")}
										{renderCreditField("sonstige_eigenmittel_als_sicherheit_eigentuemer" + index, 6, 3)}
										{renderCreditField("sonstige_eigenmittel_als_sicherheit_nachweis" + index, 6, 3)}
										<Grid item xs={12} md={12}>
											<Divider />
										</Grid>
									</React.Fragment>
								)}
								{renderCreditField("gibt_es_sicherheiten_fuer_die_finanzierung", 12, 12)}
								{renderCreditField("angaben_der_sicherheiten", 12, 12, "multiline")}
							</Grid>
						</AccordionDetails>
					</Accordion>
				}
				{credit.type === "hypothekar" ?
					<Accordion TransitionProps={{ unmountOnExit: true }}>
						<AccordionSummary
							expandIcon={<ExpandMoreIcon />}
						>
							<Typography variant="h5">Laufzeit & Termine</Typography>
						</AccordionSummary>
						<AccordionDetails>
							<Grid container spacing={4}>
								{renderCreditField("finanzierungslaufzeit", 12, 7, "months")}
								{renderCreditField("finanzierungslaufzeit_davon_tilgungsfrei", 12, 5, "months")}

								{renderCreditField("anmerkung_kredit_bis_pensionsantritt", 12)}
								{renderCreditField("faelligkeit_des_kaufpreises", 6, 2)}
								{renderCreditField("rueckzahlung_ratenfaelligkeit", 6, 2)}
								{renderCreditField("rueckzahlung_datum_der_ersten_pauschalrate", 6, 2)}
								<Grid item xs={3} md={2}>
									<FirstPaymentDateComputationHelper
										creditFunctions={creditFunctions}
									/>
								</Grid>
								<Grid item xs={12} md={12}>
									<Divider />
								</Grid>
							</Grid>
						</AccordionDetails>
					</Accordion>
					:
					<Accordion TransitionProps={{ unmountOnExit: true }}>
						{/** Laufzeit & Termine Konsumkredit */}
						<AccordionSummary
							expandIcon={<ExpandMoreIcon />}
						>
							<Typography variant="h5">Laufzeit & Termine</Typography>
						</AccordionSummary>
						<AccordionDetails>
							<Grid container spacing={4}>
								{renderCreditField("finanzierungslaufzeit", 12, 7, "months")}
							</Grid>
						</AccordionDetails>
					</Accordion>
				}
				{credit.type === "hypothekar" &&
					<Accordion TransitionProps={{ unmountOnExit: true }}>
						<AccordionSummary
							expandIcon={<ExpandMoreIcon />}
						>
							<Typography variant="h5">Treuhänder & Bankgarantie</Typography>
						</AccordionSummary>
						<AccordionDetails>
							<Grid container spacing={4}>
								<Grid item xs={12} md={6}>
									<Grid container spacing={4}>
										{renderCreditField("treuhaender_erforderlich", 12, 8)}
										{renderCreditField("treuhaender_betrag", 12, 4, "euro")}
										{renderCreditField("treuhaender_name", 12)}
										{renderCreditField("treuhaender_strasse", 8, 8)}
										{renderCreditField("treuhaender_hausnummer", 4, 4)}
										{renderCreditField("treuhaender_plz", 12, 4)}
										{renderCreditField("treuhaender_ort", 12, 8)}
										{renderCreditField("treuhaender_telefon", 12, 6)}
										{renderCreditField("treuhaender_fax", 12, 6)}
									</Grid>
								</Grid>
								<Grid item xs={12} md={6}>
									<Grid container spacing={4}>
										{renderCreditField("bankgarantie_zugunsten", 12)}
										{renderCreditField("bankgarantie_betrag", 12, 12, "euro")}
										{renderCreditField("bankgarantie_laufzeit_in_monaten", 12, 6)}
										{renderCreditField("bankgarantie_fixes_laufzeitende", 12, 6)}
									</Grid>
								</Grid>
							</Grid>
						</AccordionDetails>
					</Accordion>
				}
				{credit.type === "hypothekar" &&
					<Accordion TransitionProps={{ unmountOnExit: true }}>
						<AccordionSummary
							expandIcon={<ExpandMoreIcon />}
						>
							<Typography variant="h5">Liegenschaften</Typography>
							{[0, 1, 2].map(index =>
								<CircularProgressWithLabel percent={creditFunctions.getFieldGroupProgress("liegenschaft", index + 1)} key={index} />
							)}
						</AccordionSummary>
						<AccordionDetails className={classes.accordionDetailsWithTabs}>
							<AppBar position="static" color="default">
								<Tabs
									value={activeTabLiegenschaft}
									indicatorColor="primary"
									textColor="primary"
									onChange={(e, value) => {
										setActiveTabLiegenschaft(value);
									}}
								>{[0, 1, 2].map(index =>
									<Tab
										label={<><div>Liegenschaft {(index + 1)}</div><div>{(index === 0 ? " Finanzierungsobjekt" : " Sicherheit")}</div></>}
										key={index}
										icon={
											<CircularProgressWithLabel percent={creditFunctions.getFieldGroupProgress("liegenschaft", index + 1)} />
										}
									/>)}
								</Tabs>
							</AppBar>
							<br />

							{[0, 1, 2].map(index => {
								if (activeTabLiegenschaft === index)
									return (
										<Grid container spacing={4} role="tabpanel" key={index}>
											<CreditFieldGroupLiegenschaft
												renderCreditField={renderCreditField}
												index={index + 1}
												creditFunctions={creditFunctions}
												portals={portals}
												credit_id={id}
												metafields={metafields}
											/>
										</Grid>
									);
							})
							}
						</AccordionDetails>
					</Accordion>
				}
				{credit.type === "hypothekar" ?
					<Accordion TransitionProps={{ unmountOnExit: true }}>
						<AccordionSummary
							expandIcon={<ExpandMoreIcon />}
						>
							<Typography variant="h5">Vorlasten</Typography>
							{[0, 1, 2, 3, 4, 5].map(index =>
								<CircularProgressWithLabel percent={creditFunctions.getFieldGroupProgress("vorlast", index + 1)} key={index} />
							)}
						</AccordionSummary>
						<AccordionDetails className={classes.accordionDetailsWithTabs}>
							<AppBar position="static" color="default">
								<Tabs
									value={activeTabVorlast}
									indicatorColor="primary"
									textColor="primary"
									onChange={(e, value) => {
										setActiveTabVorlast(value);
									}}
								>{[0, 1, 2, 3, 4, 5].map(index =>
									<Tab
										label={"Vorlast " + (index + 1)}
										key={index}
										icon={
											<CircularProgressWithLabel percent={creditFunctions.getFieldGroupProgress("vorlast", index + 1)} />
										}
									/>)}
								</Tabs>
							</AppBar>
							<br />
							{[0, 1, 2, 3, 4, 5].map(index => {
								if (activeTabVorlast === index)
									return (
										<Grid container spacing={4} role="tabpanel" key={index}>
											<CreditFieldGroupVorlast
												renderCreditField={renderCreditField}
												creditFunctions={creditFunctions}
												index={index + 1}
											/>
										</Grid>
									);
							})
							}
						</AccordionDetails>
					</Accordion>
					:
					<Accordion TransitionProps={{ unmountOnExit: true }}>
						{/** Vorlasten Konsumkredit */}
						<AccordionSummary
							expandIcon={<ExpandMoreIcon />}
						>
							<Typography variant="h5">Vorlasten</Typography>
							{[0, 1, 2, 3, 4, 5].map(index =>
								<CircularProgressWithLabel percent={creditFunctions.getFieldGroupProgress("vorlast", index + 1)} key={index} />
							)}
						</AccordionSummary>
						<AccordionDetails className={classes.accordionDetailsWithTabs}>
							<AppBar position="static" color="default">
								<Tabs
									value={activeTabVorlast}
									indicatorColor="primary"
									textColor="primary"
									onChange={(e, value) => {
										setActiveTabVorlast(value);
									}}
								>{[0, 1, 2, 3, 4, 5].map(index =>
									<Tab
										label={"Vorlast " + (index + 1)}
										key={index}
										icon={
											<CircularProgressWithLabel percent={creditFunctions.getFieldGroupProgress("vorlast", index + 1)} />
										}
									/>)}
								</Tabs>
							</AppBar>
							<br />
							{[0, 1, 2, 3, 4, 5].map(index => {
								if (activeTabVorlast === index)
									return (
										<Grid container spacing={4} role="tabpanel" key={index}>
											<CreditFieldGroupVorlastKonsum
												renderCreditField={renderCreditField}
												creditFunctions={creditFunctions}
												index={index + 1}
											/>
										</Grid>
									);
							})
							}
						</AccordionDetails>
					</Accordion>
				}
				<br /><br />
				<Button
					size="small"
					variant="contained"
					startIcon={<DescriptionIcon />}
					onClick={() => {
						history.push("/finanzierung/" + id + "/dokumente");
					}}>
					Dokumente hochladen
				</Button>
				<br /><br />
				<Paper className={classes.descriptionPaper}>
					<Grid container spacing={4}>
						<Grid item xs={12}>
							<Typography variant="h5">Übertragung</Typography>
							<Typography>Übertrage deine Eingaben an die Banken.</Typography>
						</Grid>
					</Grid>
					<Grid container spacing={0}>
						{channels.map((channel, channel_index) => {
							if (channel.channel_shortname !== "GLOBAL" && channel.enabled) {
								const external_credit_id = fieldsByMetaKey
									&& fieldsByMetaKey[channel.channel_shortname + "_external_credit_id"]
									&& fieldsByMetaKey[channel.channel_shortname + "_external_credit_id"][0].meta_value
									? fieldsByMetaKey[channel.channel_shortname + "_external_credit_id"][0].meta_value
									: null;
								return (
									<Grid item key={channel.channel_shortname + "-push"}>
										<PushCreditButton
											channelDisplayNames={channelDisplayNames}
											channel_shortname={channel.channel_shortname}
											credit_id={id}
											credit_title={credit.title}
											external_credit_id={external_credit_id}
											numPersons={numPersons}
											saveExternalIDCallback={(external_credit_id) => {
												setMetafieldsValues(
													fieldsByMetaKey[channel.channel_shortname + "_external_credit_id"],
													external_credit_id,
													true
												);
											}}
										/>
									</Grid>
								);
							}
						})}
					</Grid>
				</Paper>
				<Grid container spacing={4}>
					<Grid item>
						<AssignAgentDialog
							credit={credit}
							creditFunctions={creditFunctions}
						/>
					</Grid>
					<Grid item>
						<CreditHistoryDialog
							credit={credit}
						/>
					</Grid>
					<Grid item>
						<CustomerPortalPinDialog
							creditId={id}
							portals={portals}
							onChanged={() => {
								getPortals();
							}}
						/>
					</Grid>
				</Grid>
			</div>
		</>
	);
}
